import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService } from '../auth/auth.service';
import { HostListingsService } from '../host/host-listings.service';

/**
 * Route guard for `/properties/:id/edit`. Permits the real backend
 * owner (`listing.user.id`), an admin, or — while the listing is still
 * unpublished — a sales/salesManager rep editing their own referred client's
 * listing (see `HostListingsService.checkOwnership()` for the exact rule).
 * Anonymous/no-real-id users → /auth/login with a returnTo; anyone else denied
 * → /properties. Uses a direct `GET /listing/:id` check rather than
 * the `myListings$` cache, since a cold/deep-link navigation must never
 * produce a false negative from a not-yet-hydrated cache.
 */
export const editOwnerGuard: CanActivateFn = async (route, state) => {
  const auth = inject(AuthService);
  const hostListings = inject(HostListingsService);
  const router = inject(Router);

  const user = await auth.currentUserOnceLoaded();
  if (!user?.id) {
    return router.createUrlTree(['/auth/login'], { queryParams: { returnTo: state.url } });
  }
  const idRaw = route.paramMap.get('id');
  const id = idRaw ? Number(idRaw) : NaN;
  if (!Number.isFinite(id)) return router.createUrlTree(['/properties']);

  const owns = await hostListings.checkOwnership(id, user);
  return owns ? true : router.createUrlTree(['/properties']);
};
